import React from 'react';
import { Navigate } from 'react-router-dom';
import { useRole } from '../context/RoleContext';

/**
 * RoleGuard — route-level authorization.
 *
 * Sprint 286 (M5): unauthorized navigation no longer cascades
 *   /inbox → / → /login
 * for an authenticated user. Firebase session is intact; the tech was
 * simply looking at a route they can't access. We now send them to a
 * role-appropriate landing page instead of dropping them at the login
 * screen. `signOut()` is not called by this component under any path.
 *
 * The distinction matters:
 *   • Unauthenticated → /login (handled by ProtectedRoute one level up).
 *   • Authenticated but unauthorized → this role's home.
 */

// Role → canonical authorized home. Kept in one place so callers cannot
// accidentally drift from the mobile bottom-nav config.
const ROLE_HOME = {
  owner:      '/dashboard',
  admin:      '/dashboard',
  sales:      '/dashboard',
  marketing:  '/dashboard',
  technician: '/today',
};

export function roleHome(role) {
  return ROLE_HOME[role] || '/login';
}

const RoleGuard = ({ allowedRoles, children, redirectTo }) => {
  const { role, loading, isRoleHydrating } = useRole();

  // Show a lightweight placeholder while role hydrates. The parent
  // AppReadyGate already guarantees membership resolution, but this
  // stays defensive if RoleGuard is mounted in a different tree later.
  if (loading || isRoleHydrating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Membership missing → truly unauthenticated shape. Send to login.
  if (role === null) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(role)) {
    // Authenticated but unauthorized: land on this role's home, not /login.
    const target = redirectTo || roleHome(role);
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};

export default RoleGuard;
